/**
 * Shared utility for the admin-managed wallpaper category list.
 *
 * The authoritative list lives in siteSettings/wallpaperCategories
 * (value = JSON-stringified string array, updatedAt = Unix seconds).
 *
 * This module exposes:
 *   - BASE_WALLPAPER_CATEGORIES — fallback when the DB document is missing
 *   - useWallpaperCategoriesFromDB — React hook that subscribes in real time
 *   - saveWallpaperCategoriesToDB — write the full list back to the DB
 */

import { useState, useEffect } from 'react';
import { subscribeSiteSettings, setSiteSettings } from '@/lib/collections/siteSettings';
import { Time } from '@/lib/db-client';

export const BASE_WALLPAPER_CATEGORIES = [
  'Nature', 'Abstract', 'Minimal', 'Dark', 'Gradient', 'Space', 'Urban', 'Cyberpunk',
];

/**
 * React hook — returns the live wallpaper category list from the DB.
 * Falls back to BASE_WALLPAPER_CATEGORIES if the document is missing or empty.
 * Also returns a `loading` flag while the initial subscription resolves.
 */
export function useWallpaperCategoriesFromDB(): { categories: string[]; loading: boolean } {
  const [categories, setCategories] = useState<string[]>(BASE_WALLPAPER_CATEGORIES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => Promise<void>) | null = null;

    subscribeSiteSettings((doc) => {
      setLoading(false);
      if (!doc || !doc.value) {
        setCategories(BASE_WALLPAPER_CATEGORIES);
        return;
      }
      try {
        const parsed = JSON.parse(doc.value) as unknown;
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCategories(parsed as string[]);
        } else {
          setCategories(BASE_WALLPAPER_CATEGORIES);
        }
      } catch {
        setCategories(BASE_WALLPAPER_CATEGORIES);
      }
    }, 'wallpaperCategories').then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      unsubscribe?.();
    };
  }, []);

  return { categories, loading };
}

/**
 * Write the full wallpaper category list to the DB.
 * Returns true on success, false on failure.
 */
export async function saveWallpaperCategoriesToDB(categories: string[]): Promise<boolean> {
  return setSiteSettings('wallpaperCategories', {
    value: JSON.stringify(categories),
    updatedAt: Time.Now,
  });
}

// ─── Live wallpaper categories (localStorage-backed) ──────────────────────────

const LIVE_WP_STORAGE_KEY = 'seekscape-live-wallpaper-categories';
export const BASE_LIVE_WALLPAPER_CATEGORIES: string[] = [
  'Cinematic', 'Loop', 'Nature', 'Abstract', 'Cyberpunk', 'Minimal',
];

/**
 * React hook — returns the admin-managed live wallpaper category list.
 * Reads from localStorage (same key used by ManageCategoriesSheet).
 * Subscribes to storage events so updates propagate in real time within the tab.
 */
export function useLiveWallpaperCategoriesFromStorage(): string[] {
  const [categories, setCategories] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(LIVE_WP_STORAGE_KEY);
      if (stored) return JSON.parse(stored) as string[];
    } catch {}
    return [];
  });

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== LIVE_WP_STORAGE_KEY) return;
      try {
        setCategories(e.newValue ? (JSON.parse(e.newValue) as string[]) : []);
      } catch {}
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return categories;
}

// ─── Ringtone categories (localStorage-backed) ────────────────────────────────

const RINGTONE_STORAGE_KEY = 'seekscape-ringtone-categories';
export const BASE_RINGTONE_CATEGORIES: string[] = [
  'Pop', 'Hip-Hop', 'Electronic', 'Rock', 'Ambient', 'Soundtrack', 'Lo-Fi', 'Nature Sounds',
];

/**
 * React hook — returns the admin-managed ringtone category list.
 * Reads from localStorage (same key used by ManageCategoriesSheet).
 * Subscribes to storage events so updates propagate in real time within the tab.
 */
export function useRingtomeCategoriesFromStorage(): string[] {
  const [categories, setCategories] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(RINGTONE_STORAGE_KEY);
      if (stored) return JSON.parse(stored) as string[];
    } catch {}
    return [];
  });

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== RINGTONE_STORAGE_KEY) return;
      try {
        setCategories(e.newValue ? (JSON.parse(e.newValue) as string[]) : []);
      } catch {}
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return categories;
}
